import * as React from 'react'
import { ReactElement } from 'react'
import { useFormikContext } from 'formik'
import { makeStyles, tokens } from '@fluentui/react-components'
import { LegalPersonMutationDto } from '../../../config/partyMutationConfig'
import { PartyStatusDropdown } from '../../../../components/Form/Party/PartyForm'
import { AttributeContextType, AttributesReturnProps } from '../../../../hooks/useAttributes'
import { ModuleType } from '../../../../context/GlobalContext'
import useModules from '../../../../hooks/module/useModules'
import useRoles, { Role } from '../../../../hooks/useRoles'
import { EntityType } from '../../../components/Form/Accounting/EntityField'
import MultiUnitDimensionFields from '../../../components/Form/Accounting/MultiUnitDimensionFields'
import PartyFormLayout, { PartySection } from './PartyFormLayout'
import LegalNameSection from './LegalNameSection'
import ContactSection from './ContactSection'
import AddressSection from './AddressSection'
import CrmSection from './CrmSection'
import AttributesSection from './AttributesSection'
import FinanceSection from './FinanceSection'
import TiersSection from './TiersSection'

type Props = {
    goBack: () => void
    attributes: AttributesReturnProps
}

const useStyles = makeStyles({
    formRow: { display: 'grid', gap: tokens.spacingVerticalS },
})

/** Assembles the v9 legal-person add/edit screen from the shared section components (HAM-3911). */
export default function LegalPersonFormBody(props: Props): ReactElement {
    const { initialValues, values } = useFormikContext<LegalPersonMutationDto>()
    const modules = useModules()
    const roles = useRoles()
    const styles = useStyles()

    // Also handed over while editing: the field ignores it as long as it has a stored folder, and
    // needs it the moment that folder turns out to be gone (HAM-4357).
    const suggestedName = values.name

    const mainSections: PartySection[] = [
        {
            key: 'name',
            labelKey: 'party.edit.company.label',
            content: <LegalNameSection />,
            fieldNames: ['name', 'shortName', 'legalStructure', 'cocNumber', 'cocBranchNumber'],
        },
        { key: 'contact', labelKey: 'party.edit.contact.label', content: <ContactSection />, fieldNames: ['email', 'phoneLandline'] },
        {
            key: 'address',
            labelKey: 'party.edit.address.label',
            content: <AddressSection prefix="address" />,
            fieldNames: ['addressStreet', 'addressNumber', 'addressPostalCode', 'addressLocality', 'addressMunicipality', 'addressCountry'],
        },
        {
            key: 'poBoxAddress',
            labelKey: 'party.edit.poboxaddress.label',
            content: <AddressSection prefix="poBoxAddress" />,
            fieldNames: [
                'poBoxAddressStreet',
                'poBoxAddressNumber',
                'poBoxAddressPostalCode',
                'poBoxAddressLocality',
                'poBoxAddressMunicipality',
                'poBoxAddressCountry',
            ],
        },
    ]

    const showAttributes =
        modules.has(ModuleType.Attributes) && (props.attributes.activeAttributeDefinitions.length > 0 || (values.attributes?.length ?? 0) > 0)

    const extraSections: PartySection[] = [
        modules.has(ModuleType.CRM) && {
            key: 'crm',
            labelKey: 'party.edit.crm.label',
            content: <CrmSection suggestedName={suggestedName} partyId={initialValues.id} />,
            fieldNames: ['crmRelation', 'accountManagerId', 'documentRootId', 'documentRootIntent', 'description'],
        },
        showAttributes && {
            key: 'attributes',
            labelKey: 'party.edit.attributes.label',
            content: <AttributesSection attributes={props.attributes} context={[AttributeContextType.LegalPerson]} />,
            fieldNames: ['attributes'],
        },
        modules.has(ModuleType.Invoices) && {
            key: 'finance',
            labelKey: 'party.edit.finance.label',
            content: <FinanceSection />,
            fieldNames: ['financeTermInvoice', 'financeTermPayment', 'financeEmail', 'vatNumber', 'fileReferenceRequired', 'reference'],
        },
        modules.has(ModuleType.Invoices) && { key: 'tier', labelKey: 'party.edit.tier.label', content: <TiersSection />, fieldNames: ['tiers'] },
        { key: 'other', labelKey: 'party.edit.other.label', content: <PartyStatusDropdown />, fieldNames: ['status'] },
    ].filter(Boolean) as PartySection[]

    return (
        <PartyFormLayout
            goBack={props.goBack}
            mainSections={mainSections}
            extraSections={extraSections}
            accordionExtra={
                modules.has(ModuleType.Accounting) &&
                roles.any([Role.Invoice]) && (
                    <MultiUnitDimensionFields
                        styles={styles}
                        fieldName="accounting"
                        selectedEntityTypes={[EntityType.CostCenter, EntityType.CostUnit, EntityType.GLA, EntityType.Project, EntityType.Customer]}
                    />
                )
            }
        />
    )
}
